import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  GoogleAuthProvider, 
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged
} from "firebase/auth";
import { 
  getFirestore, 
  collection, 
  addDoc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  getCountFromServer
} from "firebase/firestore";
import { 
  getStorage, 
  ref, 
  uploadString, 
  getDownloadURL, 
  deleteObject 
} from "firebase/storage";
import { getAnalytics } from "firebase/analytics";

// Firebase configuration from your console
const firebaseConfig = {
  apiKey: "AIzaSyBPaARNlhWWd6WidYiDNxlSCsuUHpU3-sw",
  authDomain: "kangleitools.firebaseapp.com",
  projectId: "kangleitools",
  storageBucket: "kangleitools.firebasestorage.app",
  messagingSenderId: "242815722716",
  appId: "1:242815722716:web:0795d1939b9665e4020019",
  measurementId: "G-ZB9J9VK6KG"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const analytics = getAnalytics(app);
const googleProvider = new GoogleAuthProvider();

// Helper function to record user activity
const recordUserActivity = async (userId, toolId, toolName) => {
  try {
    const activityRef = collection(db, 'userActivity');
    await addDoc(activityRef, {
      userId,
      toolId,
      toolName,
      timestamp: serverTimestamp()
    });
    
    // Also update the user's tools array if this is their first time using this tool
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const userTools = userData.tools || [];
      
      if (!userTools.includes(toolId)) {
        await updateDoc(userRef, {
          tools: [...userTools, toolId]
        });
      }
    }
  } catch (error) {
    console.error('Error recording user activity:', error);
  }
};

// Feature toggle functions
const getFeatureStatus = async (featureId) => {
  try {
    const featureRef = doc(db, 'featureSettings', featureId);
    const featureDoc = await getDoc(featureRef);
    
    if (featureDoc.exists()) {
      return featureDoc.data().enabled;
    } else {
      // Default to enabled if setting doesn't exist
      await setDoc(featureRef, {
        id: featureId,
        enabled: true,
        updatedAt: serverTimestamp()
      });
      return true;
    }
  } catch (error) {
    console.error(`Error getting status for feature ${featureId}:`, error);
    return true; // Default to enabled on error for better UX
  }
};

const updateFeatureStatus = async (featureId, enabled) => {
  try {
    const featureRef = doc(db, 'featureSettings', featureId);
    await setDoc(featureRef, {
      id: featureId,
      enabled,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error(`Error updating feature ${featureId}:`, error);
    throw error;
  }
};

const getAllFeatureSettings = async () => {
  try {
    const featuresRef = collection(db, 'featureSettings');
    const snapshot = await getDocs(featuresRef);
    
    const features = {};
    snapshot.forEach((doc) => {
      const data = doc.data();
      features[doc.id] = data;
    });
    
    return features;
  } catch (error) {
    console.error('Error getting all feature settings:', error);
    return {};
  }
};

// Helper function to upload a photo to Firebase Storage
const uploadPhoto = async (userId, photoData, photoId = Date.now().toString()) => {
  try {
    // Create a storage reference
    const photoRef = ref(storage, `digitalIdentity/${userId}/${photoId}`);
    
    // Upload the photo (base64 data)
    await uploadString(photoRef, photoData, 'data_url');
    
    // Get the download URL
    const downloadURL = await getDownloadURL(photoRef);
    
    return {
      success: true,
      url: downloadURL,
      photoId
    };
  } catch (error) {
    console.error('Error uploading photo:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Helper function to delete a photo from Firebase Storage
const deletePhoto = async (userId, photoId) => {
  try {
    const photoRef = ref(storage, `digitalIdentity/${userId}/${photoId}`);
    await deleteObject(photoRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting photo:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export {
  auth,
  db,
  storage,
  analytics,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  googleProvider,
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  // Firestore exports
  collection,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  getCountFromServer,
  // Storage exports
  ref,
  uploadString,
  getDownloadURL,
  deleteObject,
  // Helper functions
  recordUserActivity,
  uploadPhoto,
  deletePhoto,
  // Feature toggle functions
  getFeatureStatus,
  updateFeatureStatus,
  getAllFeatureSettings
};
